import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { reportService, qrCodeService } from '../services/api';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LabelList
} from 'recharts';
import BrazilMap from '../components/BrazilMap';
import './Reports.css';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        padding: '0.75rem 1rem',
        border: '1px solid var(--secondary-200)',
        borderRadius: '8px',
        boxShadow: 'var(--shadow-custom)',
        backdropFilter: 'blur(4px)'
      }}>
        <p style={{
          fontWeight: 600,
          marginBottom: '0.25rem',
          color: 'var(--secondary-800)',
          fontSize: '0.9rem'
        }}>{label}</p>

        {payload.map((entry, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: entry.stroke || entry.fill
            }}></span>
            <span style={{
              color: 'var(--secondary-600)',
              fontSize: '0.85rem',
              fontWeight: 500
            }}>
              {entry.name}: <strong style={{ color: 'var(--primary-600)' }}>{entry.value}</strong>
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Componente Wrapper para Gráficos vazios
const ChartWrapper = ({ children, data, height = 300 }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '8px', color: '#94a3b8' }}>
        Sem dados para exibir
      </div>
    );
  }
  return <ResponsiveContainer width="100%" height={height}>{children}</ResponsiveContainer>;
};

function Reports({ onLogout }) {
  const [reports, setReports] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [selectedQR, setSelectedQR] = useState('');
  // Inicializar com últimos 30 dias
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedView, setSelectedView] = useState('geral'); // 'geral', 'localizacao', 'detalhado'
  const [quickFilter, setQuickFilter] = useState('30d'); // 30d, 3m, 6m, 1y, all

  useEffect(() => {
    loadQRCodes();
    loadReports();
  }, []); // Carrega ao montar, usando os estados iniciais de data

  const handleQuickDateSelect = (e) => {
    const value = e.target.value;
    setQuickFilter(value);

    const today = new Date();
    let start = new Date();

    switch (value) {
      case '30d':
        start.setDate(today.getDate() - 30);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case '3m':
        start.setMonth(today.getMonth() - 3);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case '6m':
        start.setMonth(today.getMonth() - 6);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case '1y':
        start.setFullYear(today.getFullYear() - 1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'all':
        setStartDate('');
        setEndDate('');
        break;
      default:
        break;
    }
  };

  const getFormattedDateRange = () => {
    if (!startDate || !endDate) return 'Todo o período';
    const start = new Date(startDate).toLocaleDateString('pt-BR');
    const end = new Date(endDate).toLocaleDateString('pt-BR');
    return `${start} até ${end}`;
  };

  const loadQRCodes = async () => {
    try {
      const data = await qrCodeService.list();
      setQrCodes(data);
    } catch (err) {
      console.error('Erro ao carregar QR Codes:', err);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedQR) filters.qrId = selectedQR;
      if (startDate) filters.startDate = new Date(startDate).toISOString();
      if (endDate) filters.endDate = new Date(endDate + 'T23:59:59').toISOString();

      const data = await reportService.get(filters);

      if (data && data.reports) {
        setReports(data.reports);
        setGlobalStats(data.globalStats);
      } else {
        setReports(Array.isArray(data) ? data : []);
        setGlobalStats(null);
      }
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
      setError('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (e) => {
    e.preventDefault();
    loadReports();
  };

  const getTotalAccesses = () => {
    return reports.reduce((sum, report) => sum + report.totalAcessos, 0);
  };

  const getTotalAccessesWithLocation = () => {
    return reports.reduce((sum, report) => sum + (report.acessosComLocalizacao || 0), 0);
  };

  const getChartData = () => {
    if (reports.length === 0) return [];

    const allDates = new Set();
    reports.forEach(report => {
      if (report.acessosPorDia) {
        Object.keys(report.acessosPorDia).forEach(date => allDates.add(date));
      }
    });

    const sortedDates = Array.from(allDates).sort();

    // Identificar Top 5 para exibir no gráfico
    const topQRs = [...reports].sort((a, b) => b.totalAcessos - a.totalAcessos).slice(0, 5);

    return sortedDates.map(date => {
      const formattedDate = new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      const dataPoint = {
        rawDate: date,
        date: formattedDate
      };

      if (selectedQR) {
        // Se tem QR selecionado, mostra apenas ele
        reports.forEach(report => {
          if (report.qrId === selectedQR) {
            dataPoint[report.nome || report.qrId] = report.acessosPorDia ? (report.acessosPorDia[date] || 0) : 0;
          }
        });
      } else {
        // Visão Geral: Mostra os Top 5 individualmente
        topQRs.forEach(report => {
          dataPoint[report.nome || report.qrId] = report.acessosPorDia ? (report.acessosPorDia[date] || 0) : 0;
        });
      }

      return dataPoint;
    });
  };

  const getTopStates = () => {
    if (selectedQR && reports.length > 0) {
      const report = reports.find(r => r.qrId === selectedQR);
      return report?.acessosPorEstado || [];
    }
    return globalStats?.topEstados || [];
  };

  const getTopCities = () => {
    if (selectedQR && reports.length > 0) {
      const report = reports.find(r => r.qrId === selectedQR);
      return report?.acessosPorCidade || [];
    }
    return globalStats?.topCidades || [];
  };

  const getAllCities = () => {
    const allCities = [];
    reports.forEach(report => {
      if (report.acessosPorCidade) {
        allCities.push(...report.acessosPorCidade);
      }
    });
    return allCities;
  };

  const getLocationPercentage = () => {
    const total = getTotalAccesses();
    const withLocation = getTotalAccessesWithLocation();
    return total > 0 ? ((withLocation / total) * 100).toFixed(1) : 0;
  };

  const chartData = getChartData();

  // Preparar dados para o gráfico de barras (Top QR Codes)
  // Filtra itens inválidos e garante display name
  const barChartData = reports
    .filter(r => r.qrId && r.totalAcessos > 0)
    .map(r => ({
      ...r,
      displayName: r.nome || r.qrId || 'Sem Nome'
    }))
    .sort((a, b) => b.totalAcessos - a.totalAcessos)
    .slice(0, 10);

  return (
    <div className="reports-page">
      <Navigation onLogout={onLogout} />

      <div className="container reports-container">
        <div className="reports-header">
          <h1>Relatórios e Analytics</h1>
          <p className="reports-subtitle">
            Acompanhe o desempenho dos seus QR Codes em tempo real.
            <span className="date-range-badge">📅 Exibindo dados de: <strong>{getFormattedDateRange()}</strong></span>
          </p>
        </div>

        {/* Filtros Modernos */}
        <div className="card filters-card">
          <form onSubmit={handleFilter} className="filter-form">
            <div className="form-group">
              <label>Filtrar por QR Code</label>
              <select value={selectedQR} onChange={(e) => setSelectedQR(e.target.value)}>
                <option value="">Todos os QR Codes</option>
                {qrCodes.map(qr => (
                  <option key={qr.id} value={qr.id}>
                    {qr.nome || qr.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Período Rápido</label>
              <select value={quickFilter} onChange={handleQuickDateSelect}>
                <option value="30d">Últimos 30 dias</option>
                <option value="3m">Últimos 3 meses</option>
                <option value="6m">Últimos 6 meses</option>
                <option value="1y">Último ano</option>
                <option value="all">Todo o tempo</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>

            <div className="form-group">
              <label>Data inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setQuickFilter('custom');
                }}
              />
            </div>

            <div className="form-group">
              <label>Data final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setQuickFilter('custom');
                }}
              />
            </div>

            <button type="submit" className="btn btn-primary" style={{ marginBottom: '1.5rem' }}>
              Aplicar Filtros
            </button>
          </form>
        </div>

        {/* Tabs de visualização */}
        <div className="view-tabs">
          <button
            className={`view-tab ${selectedView === 'geral' ? 'active' : ''}`}
            onClick={() => setSelectedView('geral')}
          >
            Visão Geral
          </button>
          <button
            className={`view-tab ${selectedView === 'localizacao' ? 'active' : ''}`}
            onClick={() => setSelectedView('localizacao')}
          >
            Geolocalização
          </button>
          <button
            className={`view-tab ${selectedView === 'detalhado' ? 'active' : ''}`}
            onClick={() => setSelectedView('detalhado')}
          >
            Tabela Detalhada
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Processando dados...</p>
          </div>
        ) : (
          <>
            {error && <div className="error">{error}</div>}

            {reports.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <p>Nenhum dado encontrado para os filtros selecionados.</p>
              </div>
            ) : (
              <>
                {/* VISÃO GERAL */}
                {selectedView === 'geral' && (
                  <div className="view-content">
                    {/* Cards de estatísticas */}
                    <div className="stats-grid">
                      <div className="stat-card primary">
                        <div className="stat-icon">👁️</div>
                        <div className="stat-content">
                          <h3>Total de Acessos</h3>
                          <p className="stat-number">{getTotalAccesses().toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="stat-card success">
                        <div className="stat-icon">📍</div>
                        <div className="stat-content">
                          <h3>Com Localização</h3>
                          <p className="stat-number">{getTotalAccessesWithLocation().toLocaleString()}</p>
                          <small>{getLocationPercentage()}% Rastreabilidade</small>
                        </div>
                      </div>

                      <div className="stat-card info">
                        <div className="stat-icon">Q</div>
                        <div className="stat-content">
                          <h3>QR Codes Ativos</h3>
                          <p className="stat-number">{reports.length}</p>
                        </div>
                      </div>

                      <div className="stat-card warning">
                        <div className="stat-icon">🏙️</div>
                        <div className="stat-content">
                          <h3>Alcance (Cidades)</h3>
                          <p className="stat-number">{globalStats?.topCidades?.length || getAllCities().length}</p>
                        </div>
                      </div>
                    </div>

                    {/* Gráfico de Área (Evolução) */}
                    <div className="card chart-card">
                      <h2>Evolução de Acessos</h2>
                      <ChartWrapper data={chartData} height={350}>
                        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            {/* Gradientes Dinâmicos */}
                            {COLORS.map((color, index) => (
                              <linearGradient key={`grad-${index}`} id={`color-${index}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                              </linearGradient>
                            ))}
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis
                            dataKey="date"
                            stroke="#94a3b8"
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                            dy={10}
                          />
                          <YAxis
                            stroke="#94a3b8"
                            fontSize={12}
                            tickLine={false}
                            axisLine={false}
                            dx={-10}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />

                          {selectedQR ? (
                            // Gráfico Único (Quando filtrado)
                            (() => {
                              const report = reports.find(r => r.qrId === selectedQR);
                              const name = report ? (report.nome || report.qrId) : 'Acessos';
                              return (
                                <Area
                                  key="selected"
                                  type="monotone"
                                  dataKey={name}
                                  stroke="#6366f1"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#color-0)"
                                  activeDot={{ r: 6, strokeWidth: 0, fill: '#6366f1' }}
                                />
                              );
                            })()
                          ) : (
                            // Gráfico Top 5 (Visão Geral)
                            [...reports]
                              .sort((a, b) => b.totalAcessos - a.totalAcessos)
                              .slice(0, 5)
                              .map((report, index) => (
                                <Area
                                  key={report.qrId}
                                  type="monotone"
                                  dataKey={report.nome || report.qrId}
                                  stroke={COLORS[index % COLORS.length]}
                                  strokeWidth={2}
                                  fillOpacity={1}
                                  fill={`url(#color-${index})`}
                                  stackId="1"
                                />
                              ))
                          )}
                        </AreaChart>
                      </ChartWrapper>
                    </div>

                    {/* Grid com gráficos de barras e pizza */}
                    <div className="charts-row">
                      <div className="card chart-card">
                        <h2>Top QR Codes por Acessos</h2>
                        <ChartWrapper data={barChartData} height={350}>
                          <BarChart
                            data={barChartData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                            barSize={24}
                          >
                            <defs>
                              <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#818cf8" />
                                <stop offset="100%" stopColor="#4f46e5" />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                            <XAxis type="number" hide />
                            <YAxis
                              dataKey="displayName"
                              type="category"
                              width={120}
                              stroke="#64748b"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                              tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
                            />
                            <Tooltip
                              content={<CustomTooltip />}
                              cursor={{ fill: 'rgba(241, 245, 249, 0.6)' }}
                            />
                            <Bar
                              dataKey="totalAcessos"
                              radius={[0, 4, 4, 0]}
                              fill="url(#barGradient)"
                            >
                              {/* Label com o valor ao final da barra */}
                              <LabelList dataKey="totalAcessos" position="right" fill="#64748b" fontSize={12} fontWeight={600} />

                              {/* Cores individuais opcionais se preferir colorido */}
                              {barChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} fillOpacity={0.9} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ChartWrapper>
                      </div>

                      <div className="card chart-card">
                        <h2>Top 5 Estados</h2>
                        <ChartWrapper data={globalStats?.topEstados} height={300}>
                          <PieChart>
                            <Pie
                              data={globalStats?.topEstados?.slice(0, 5)}
                              dataKey="count"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={5}
                            >
                              {globalStats?.topEstados?.slice(0, 5).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend layout="vertical" verticalAlign="middle" align="right" />
                          </PieChart>
                        </ChartWrapper>
                      </div>
                    </div>
                  </div>
                )}

                {/* VISÃO DE GEOLOCALIZAÇÃO */}
                {selectedView === 'localizacao' && (
                  <div className="view-content">
                    <div className="location-grid">
                      {/* Mapa do Brasil */}
                      <div className="card map-card">
                        <h2>Mapa de Calor - Brasil</h2>
                        <div className="map-container">
                          <BrazilMap
                            stateData={getTopStates()}
                            cityData={getTopCities()}
                          />
                        </div>
                      </div>

                      {/* Ranking de Estados e Cidades */}
                      <div className="rankings">
                        <div className="card ranking-card">
                          <h3>Top Estados</h3>
                          <div className="ranking-list">
                            {getTopStates().slice(0, 10).map((state, index) => (
                              <div key={state.code} className="ranking-item">
                                <span className="ranking-position">#{index + 1}</span>
                                <span className="ranking-name">{state.name}</span>
                                <span className="ranking-badge">{state.count}</span>
                              </div>
                            ))}
                            {getTopStates().length === 0 && <p className="no-data">Sem dados</p>}
                          </div>
                        </div>

                        <div className="card ranking-card">
                          <h3>Top Cidades</h3>
                          <div className="ranking-list">
                            {getTopCities().slice(0, 10).map((city, index) => (
                              <div key={index} className="ranking-item">
                                <span className="ranking-position">#{index + 1}</span>
                                <span className="ranking-name">{city.city}, {city.stateCode}</span>
                                <span className="ranking-badge">{city.count}</span>
                              </div>
                            ))}
                            {getTopCities().length === 0 && <p className="no-data">Sem dados</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* VISÃO DETALHADA */}
                {selectedView === 'detalhado' && (
                  <div className="view-content">
                    <div className="card">
                      <h2>Relatório Detalhado</h2>
                      <div className="table-responsive">
                        <table className="report-table">
                          <thead>
                            <tr>
                              <th>QR Code</th>
                              <th>ID</th>
                              <th>Acessos Totais</th>
                              <th>Rastreados</th>
                              <th>Perfomance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map(report => {
                              const percentage = report.totalAcessos > 0
                                ? ((report.acessosComLocalizacao || 0) / report.totalAcessos * 100).toFixed(1)
                                : 0;

                              return (
                                <tr key={report.qrId}>
                                  <td><strong>{report.nome || 'Sem Nome'}</strong></td>
                                  <td style={{ fontFamily: 'monospace', color: 'var(--secondary-500)' }}>{report.qrId}</td>
                                  <td><span className="badge badge-primary">{report.totalAcessos}</span></td>
                                  <td><span className="badge badge-success">{report.acessosComLocalizacao || 0}</span></td>
                                  <td style={{ width: '200px' }}>
                                    <div className="progress-bar-container" title={`${percentage}% rastreado`}>
                                      <div className="progress-bar" style={{ width: `${percentage}%` }}></div>
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--secondary-500)' }}>{percentage}% rastreabilidade</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Reports;
